export interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  avatar?: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  MODERATOR = 'moderator',
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string | null;
}

export interface AuthState {
  user: User | null;
  role: 'admin' | 'user' | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  confirmPassword: string;
}

export interface ApiResponse<T = unknown> {
  data: T;
  message: string;
  success: boolean;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Theme {
  id: string;
  name: string;
  colors: {
    background: string;
    foreground: string;
    primary: string;
    secondary: string;
    accent: string;
    muted: string;
    border: string;
    destructive: string;
  };
}

export type ThemeMode = 'light' | 'dark' | 'system';

export interface FeatureFlags {
  betaFeatures: boolean;
  advancedAnalytics: boolean;
  experimentalUI: boolean;
}

export type Language = 'en' | 'es';

export interface TableColumn<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (row: T) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
}

export interface FormStep {
  id: string;
  title: string;
  description?: string;
  component: React.ComponentType<any>;
  validation?: any;
}

export interface ContentCategory {
  id: number;
  title: string;
  slug: string;
  subslug?: string;
  iconUrl: string;
  description: string;
  benefits: string | null;
  isLocked: boolean;
  contentCount?: number;
  totalPlays?: number;
}
export interface Admin {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  profileImage: string | null;
  status: 'Active' | 'Inactive';
  createdAt: string;
  updatedAt: string;
  dialCode: string;
  mobile: string;
  country: string;
}

export interface ProfileFormData {
  firstname: string;
  lastname: string;
  email: string;

  country: string;
  dialCode: string;
  phone: string;
}

export interface GetContentsParams {
  isLocked: boolean;
  skip?: number;
  take?: number;
  search?: string;
  fromDate?: string;
  toDate?: string;
  categoryId?: number;
}

export interface GetContentsParams2 {
  skip?: number;
  take?: number;
  search?: string;
  fromDate?: string;
  toDate?: string;
  categoryId?: number;
}
export interface PaginatedContentResponse {
  data: ContentResponse[];
  count: number;
  skip: number;
  take: number;
}
export interface ContentResponse {
  id: number;
  title: string;
  slug: string;
  description: string;
  benefits: string | null;
  sanskritMantra?: string | null;
  englishTranslation?: string | null;
  englishPronunciation?: string | null;
  sourceType: string;
  cachedAudioUrl: string | null;
  audioUrl: string;
  qrUrl: string;
  videoUrl: string | null;
  embedUrl: string | null;
  productLink?: string | null;
  personalizedProductLink?: string | null;
  iconUrl?: string | null;
  imageUrl?: string | null;
  audioKey?: string | null;
  uploadToken: string;
  videoDuration?: number;
  duration: number;
  playCount: number;
  categoryId: number;
  createdAt: string;
  updatedAt: string;
  category?: {
    id: number;
    title: string;
    slug: string;
    iconUrl: string;
    description: string;
    benefits: string;
    isLocked: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

export interface UpdateContentRequest {
  id: number;
  title?: string;
  description?: string;
  benefits?: string | null;
  categoryId?: number;
  sanskritMantra?: string | null;
  englishTranslation?: string | null;
  englishPronunciation?: string | null;
  sourceType?: string | null;
  audioUploadToken?: string | null;
  audioKey?: string | null;
  audioUrl?: string | null;
  duration?: number | null;
  uploadToken?: string | null;
  videoDuration?: number | null;
  videoFile?: File;
  embedUrl?: string | null;
  videoUrl?: string | null;
  productLink?: string | null;
  personalizedProductLink?: string | null;
  iconUrl?: string | null;
  imageUrl?: string | null;
  file?: File;
  icon?: File;
  image?: File;
}
export interface ApiContent {
  id: number;
  title: string;
  slug: string;
  description: string;
  sanskritMantra?: string | null;
  englishTranslation?: string | null;
  englishPronunciation?: string | null;
  sourceType: string;
  audioKey: string | null;
  fileUrl: string | null;
  embedUrl: string | null;
  iconUrl: string | null;
  imageUrl?: string | null;
  cachedAudioUrl: string | null;
  qrUrl: string | null;
  productLink?: string | null;
  personalizedProductLink?: string | null;
  uploadToken: string | null;
  duration: number;
  playCount: number;
  categoryId: number;
}
export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export interface UserManagent {
  id: number;
  fullName: string;
  email: string;
  profileImage: string | null;
  isVerified: boolean;
  status: 'Active' | 'Inactive' | string;
  updatedAt: string;
}

export interface GetUsersResponse {
  count: number;
  skip: number;
  take: number;
  data: UserManagent[];
}

export interface GetUsersParams {
  skip?: number;
  take?: number;
  search?: string;
  userRegisterType?: string;
}

export type UploadResponse = {
  meta: {
    originalname: string;
    mimetype: string;
    size: number;
    url: string;
    audioKey?: string;
    fileUrl?: string;
    iconUrl?: string;
    imageUrl?: string;
    duration: number;
    videoDuration?: number;
    uploadToken?: string;
    type: string;
  };
};

export interface DashboardOverview {
  totalUsers: number;
  totalPlays: number;
  qrScans: number;
  totalUnlocks: number;
  totalFreeUsers: number;
  totalPremiumUsers: number;
}

export interface TopPlayedMantra {
  contentId: number;
  title: string;
  imageUrl: string | null;
  contentType: string;
  playCount: number;
  totalPlays: number;
}

export interface DashboardResponse {
  overview: DashboardOverview;
  topPlayedMantras: TopPlayedMantra[];
}
